import { Injectable } from '@angular/core';
import { APIService } from '../api.service';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/browser';
import { PromotionModel } from 'src/app/model/promotion.model';

@Injectable({
  providedIn: 'root',
})
export class ApiTrackingService {
  constructor(private apiService: APIService) {}

  /**
   * Crea un nuevo evento de tracking
   * @param cardNumber Numero de la tarjeta
   * @param enrollUser -1: Si no esta fidelizado, 0: Si esta fidelizado, 1: Si se fidelizo en el proceso
   * @param recovery Booleano que indica si el proceso es de recuperación de trackeo
   * @param lastStepReached Ultimo paso del proceso
   * @param email: Email del usuario
   */
  async createSession(cardNumber: string, enrollUser: { email: string }, recovery?: boolean, lastStepReached?: string): Promise<void> {
    try {
      const sessionTransaction = sessionStorage.getItem('tracking-session');
      if (sessionTransaction !== null) {
        // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
        const session = JSON.parse(sessionTransaction);

        // Comprobar si la tarjeta es igual al de la session anterior
        if (session?.card.toString() !== cardNumber) {
          // Se elimina el registro de la session anterior
          sessionStorage.removeItem('tracking-session');

          // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
          const params = {
            uid: uuidv4(),
            card: Number(cardNumber),
            initialDate: DateTime.now().toMillis(),
            finalDate: null,
            lastStep: recovery ? lastStepReached : 'PORTAL DE PROMOCIONES',
            paymentType: null,
            fidelity: enrollUser === null ? 'SIN FIDELIZAR' : 'FIDELIZADO PREVIAMENTE',
            registeredEmail: enrollUser === null ? 'no-email@happyland.com.pe' : enrollUser.email,
            paymentAmount: 0,
            promotionCash: 0,
            promotionCashBonus: 0,
            promotionTickets: 0,
            promotionTokens: 0,
            promotionTokenBonus: 0,
          };

          sessionStorage.setItem('tracking-session', JSON.stringify(params));
          await this.apiService.CreateTrackingUsers({
            uid: params.uid,
            cardNumber: Number(params.card),
            initialDate: params.initialDate,
            finalDate: DateTime.now().toMillis(),
            lastStepReached: params.lastStep,
            paymentMethod: params.paymentType,
            fidelity: params.fidelity,
            registeredEmail: params.registeredEmail,
            paymentAmount: 0,
            promotionCash: 0,
            promotionCashBonus: 0,
            promotionTickets: 0,
            promotionTokens: 0,
            promotionTokenBonus: 0,
          });
        } else {
          // Detectar si existe una diferencia entre la hora de inicio y la hora actual de mas de 10 minutos con luxon
          const difference = DateTime.now().diff(DateTime.fromMillis(session.initialDate)).as('minutes');
          // Diferencia de minutos: ${difference}

          // Se comprueba si la diferencia es mayor a 10 minutos, en caso de ser asi, se crea un nuevo evento de tracking
          if (difference > 10) {
            // console.log('PASO: REANUDANDO PROCESO - HORA DE INICIO SUPERIOR A 10 MINUTOS')

            const params = {
              uid: uuidv4(),
              cardNumber: Number(session.card),
              initialDate: Number(session.initialDate),
              finalDate: DateTime.now().toMillis(),
              lastStepReached: `${session.lastStep}`,
              paymentMethod: `${session.paymentType}`,
              fidelity: `${session.fidelity}`,
              registeredEmail: session?.registeredEmail !== undefined ? session?.registeredEmail : enrollUser === null ? 'no-email@happyland.com.pe' : enrollUser.email,
              paymentAmount: 0,
              promotionCash: 0,
              promotionCashBonus: 0,
              promotionTickets: 0,
              promotionTokens: 0,
              promotionTokenBonus: 0,
            };

            // console.log(params)
            await this.apiService.CreateTrackingUsers(params);
          }
        }
      } else {
        // Se elimina el registro de la session anterior
        sessionStorage.removeItem('tracking-session');

        // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
        const params = {
          uid: uuidv4(),
          card: Number(cardNumber),
          initialDate: DateTime.now().toMillis(),
          finalDate: null,
          lastStep: 'PORTAL DE PROMOCIONES',
          paymentType: null,
          fidelity: enrollUser === null ? 'SIN FIDELIZAR' : 'FIDELIZADO PREVIAMENTE',
          registeredEmail: enrollUser === null ? null : enrollUser.email,
          paymentAmount: 0,
          promotionCash: 0,
          promotionCashBonus: 0,
          promotionTickets: 0,
          promotionTokens: 0,
          promotionTokenBonus: 0,
        };

        sessionStorage.setItem('tracking-session', JSON.stringify(params));
        await this.apiService.CreateTrackingUsers({
          uid: params.uid,
          cardNumber: Number(params.card),
          initialDate: params.initialDate,
          finalDate: DateTime.now().toMillis(),
          lastStepReached: params.lastStep,
          paymentMethod: params.paymentType,
          fidelity: params.fidelity,
          registeredEmail: params.registeredEmail,
          paymentAmount: 0,
          promotionCash: 0,
          promotionCashBonus: 0,
          promotionTickets: 0,
          promotionTokens: 0,
          promotionTokenBonus: 0,
        });
      }
    } catch (errCritical) {
      Sentry.captureException(errCritical);
      // console.error(errCritical);
    }
  }

  /**
   * Actualiza el evento de tracking
   * @param lastStep Ultimo paso del proceso
   * @param paymentMethod  Método de pago
   * @param enrollStatus Booleano que indica si el usuario se fidelizo o no
   * @returns
   */
  async updateSessionOnlyStep(lastStep: string, amount?: number): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      const session = JSON.parse(sessionTracking);

      const params = {
        uid: session.uid,
        card: session.card,
        initialDate: session.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStep: lastStep,
        paymentType: session.paymentType,
        fidelity: session.fidelity,
        registeredEmail: session.registeredEmail,
        paymentAmount: amount ? amount : session.paymentAmount,
        promotionCash: session.promotionCash,
        promotionCashBonus: session.promotionCashBonus,
        promotionTickets: session.promotionTickets,
        promotionTokens: session.promotionTokens,
        promotionTokenBonus: session.promotionTokenBonus,
      };

      // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
      sessionStorage.setItem('tracking-session', JSON.stringify(params));

      await this.apiService.UpdateTrackingUsers({
        uid: params.uid,
        cardNumber: Number(params.card),
        initialDate: params.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStepReached: params.lastStep,
        paymentMethod: params.paymentType,
        fidelity: params.fidelity,
        registeredEmail: params.registeredEmail,
        paymentAmount: session.paymentAmount,
        promotionCash: session.promotionCash,
        promotionCashBonus: session.promotionCashBonus,
        promotionTickets: session.promotionTickets,
        promotionTokens: session.promotionTokens,
        promotionTokenBonus: session.promotionTokenBonus,
      });

      return Promise.resolve();
    }
  }

  /**
   * Actualiza el evento de tracking
   * @param lastStep Ultimo paso del proceso
   * @param paymentMethod  Método de pago
   * @param enrollStatus Booleano que indica si el usuario se fidelizo o no
   * @returns
   */
  async updateSessionStepWithPayment(
    cardIdentification: string,
    lastStep: string,
    paymentMethod: string,
    enrollStatus: boolean,
    promotion: PromotionModel,
    email?: string
  ): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      const session = JSON.parse(sessionTracking);

      const params = {
        uid: session.uid,
        card: session.card,
        initialDate: session.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStep: lastStep,
        paymentType: paymentMethod,
        fidelity: session.fidelity === 'SIN FIDELIZAR' ? (enrollStatus ? 'FIDELIZADO RECIENTEMENTE' : session.fidelity) : session.fidelity,
        registeredEmail: session.fidelity === 'SIN FIDELIZAR' ? email : session.fidelity === 'FIDELIZADO RECIENTEMENTE' ? email : session.email,
        paymentAmount: promotion.amount,
        promotionCash: promotion.amount,
        promotionCashBonus: promotion.bonusAmount,
        promotionTickets: promotion.tickets,
        promotionTokens: promotion.token,
        promotionTokenBonus: promotion.bonusToken,
      };

      // Se configura el sessionStorage para ir marcando los pasos del proceso por tarjeta
      sessionStorage.setItem('tracking-session', JSON.stringify(params));

      await this.apiService.UpdateTrackingUsers({
        uid: params.uid,
        cardNumber: Number(params.card),
        initialDate: params.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStepReached: params.lastStep,
        paymentMethod: params.paymentType,
        fidelity: params.fidelity,
        registeredEmail: params.registeredEmail,
        paymentAmount: session.paymentAmount,
        promotionCash: session.promotionCash,
        promotionCashBonus: session.promotionCashBonus,
        promotionTickets: session.promotionTickets,
        promotionTokens: session.promotionTokens,
        promotionTokenBonus: session.promotionTokenBonus,
      });

      return Promise.resolve();
    } else {
      this.createSession(cardIdentification, null, true, lastStep);
    }
  }

  /**
   * Finaliza el evento de tracking
   */
  async finalizeSession(lastStep: string): Promise<void> {
    // Actualizando Session del usuario
    const sessionTracking = sessionStorage.getItem('tracking-session');

    if (sessionTracking !== null) {
      const session = JSON.parse(sessionTracking);

      const params = {
        uid: session.uid,
        card: session.card,
        initialDate: session.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStep: session.lastStep,
        paymentType: session.paymentType,
        fidelity: session.fidelity,
        registeredEmail: session.registeredEmail,
        paymentAmount: session.paymentAmount,
        promotionCash: session.promotionCash,
        promotionCashBonus: session.promotionCashBonus,
        promotionTickets: session.promotionTickets,
        promotionTokens: session.promotionTokens,
        promotionTokenBonus: session.promotionTokenBonus,
      };

      await this.apiService.UpdateTrackingUsers({
        uid: params.uid,
        cardNumber: Number(params.card),
        initialDate: params.initialDate,
        finalDate: DateTime.now().toMillis(),
        lastStepReached: lastStep,
        paymentMethod: params.paymentType,
        fidelity: params.fidelity,
        registeredEmail: params.registeredEmail,
        paymentAmount: session.paymentAmount,
        promotionCash: session.promotionCash,
        promotionCashBonus: session.promotionCashBonus,
        promotionTickets: session.promotionTickets,
        promotionTokens: session.promotionTokens,
        promotionTokenBonus: session.promotionTokenBonus,
      });

      sessionStorage.clear();
      return Promise.resolve();
    }
  }
}
